/**
 * Auto-save and restore hook for dosimetry state.
 *
 * Mirrors useStrategyPersistence: restores on INITIALIZED,
 * marks dirty on mutation events, saves via workflowState.upsert.
 */

import { useEffect, useRef, useState } from 'react';

import type { IDosimetryStateDTO } from '../services/dosimetry-state-service';
import { DosimetryStateService } from '../services/dosimetry-state-service';

import { useApi, useVisualization } from '@/context';
import { useAutoSave, type UseAutoSaveResult } from '@/hooks/use-auto-save';
import { notifyWorkflowSaveRefused } from '@/lib/workflow-save-error';
import { ApiError } from '@/services';

const WORKFLOW_STATE_KEY = 'dosimetry-state';

// ============================================================================
// Types
// ============================================================================

export interface UseDosimetryPersistenceOptions {
    caseId: string;
}

export type UseDosimetryPersistenceResult = Pick<
    UseAutoSaveResult,
    'status' | 'saveNow' | 'conflict' | 'dismissConflict' | 'lastSavedAt' | 'retryCount'
> & {
    restoreError: Error | null;
};

// ============================================================================
// Hook
// ============================================================================

export function useDosimetryPersistence(
    options: UseDosimetryPersistenceOptions
): UseDosimetryPersistenceResult {
    const { caseId } = options;
    const api = useApi();
    const { getServiceManager } = useVisualization();
    const isRestoredRef = useRef(false);
    const [restoreError, setRestoreError] = useState<Error | null>(null);

    // Resolve service from the ServiceManager
    const sm = getServiceManager();
    const dosimetryService = sm.getService<DosimetryStateService>(DosimetryStateService.SERVICE_NAME);

    // ── Auto-save setup ──
    const autoSave = useAutoSave<IDosimetryStateDTO>({
        debounceMs: 1500,
        maxRetries: 3,
        // A 403 refusal must reach the person losing the edits (ESCLOUD-288).
        onError: notifyWorkflowSaveRefused,
        getState: () => dosimetryService.serializeState(),
        saveFn: async (state, version) => {
            const response = await api.workflowState.upsert(caseId, WORKFLOW_STATE_KEY, {
                state,
                version,
            });
            return { version: response.version };
        },
    });

    // ── Restore state from API after initialization ──
    useEffect(() => {
        const restoreFromApi = async () => {
            if (isRestoredRef.current) return;
            isRestoredRef.current = true;

            try {
                const saved = await api.workflowState.getByKey(caseId, WORKFLOW_STATE_KEY);
                if (saved) {
                    autoSave.setVersion(saved.version);
                    const dto = saved.state as IDosimetryStateDTO;
                    dosimetryService.loadState(dto);
                }
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) {
                    return;
                }
                console.error('[DosimetryPersistence] Failed to restore state:', error);
                setRestoreError(error instanceof Error ? error : new Error(String(error)));
            }
        };

        const unsubscribe = dosimetryService.subscribe(dosimetryService.EVENTS.INITIALIZED, restoreFromApi);

        if (dosimetryService.isInitialized()) {
            restoreFromApi();
        }

        return () => {
            unsubscribe();
            isRestoredRef.current = false;
        };
    }, [caseId, dosimetryService]);

    // ── Subscribe to mutation events -> markDirty ──
    useEffect(() => {
        const unsubscribers = dosimetryService.MUTATION_EVENTS.map((event) =>
            dosimetryService.subscribe(event, autoSave.markDirty)
        );

        return () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [dosimetryService, autoSave.markDirty]);

    return {
        status: autoSave.status,
        saveNow: autoSave.saveNow,
        conflict: autoSave.conflict,
        dismissConflict: autoSave.dismissConflict,
        lastSavedAt: autoSave.lastSavedAt,
        retryCount: autoSave.retryCount,
        restoreError,
    };
}
